import React, { useState, useEffect, useMemo, useCallback } from "react";
import Container from "../../components/Container";
import Button from "../../components/Button";
import { motion, AnimatePresence } from "framer-motion";
import { Tooltip } from "react-tooltip";
import useFetchDobornye from './useFetchDobornye.js';

const PATH = process.env.REACT_APP_ROOF_PATH_TO_BACKEND;

const formatNumber = (number) => {
  return number
    .toFixed(2)
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

function Offer({
  typeRoof,
  typeOptions = [],
  selectedNomenclature = {},
  dealId,
  distanceFromFactory,
  region,
  sum
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [initialRows, setInitialRows] = useState([]);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  const [hasChanges, setHasChanges] = useState(false);
  const currentRoof = typeOptions.find((roof) => roof.value === typeRoof);
  const { dobornye } = useFetchDobornye(PATH);

  // Функция валидации для вальмовой кровли
  const validateHipRoof = () => {
    if (typeRoof === "hip") {
      const params = currentRoof?.parameters;
      const w1 = parseFloat(params?.find(p => p.name.includes("Ширина ската 1 (W1)"))?.value) || 0;
      const w3 = parseFloat(params?.find(p => p.name.includes("Ширина конька (W3)"))?.value) || 0;
      const h2 = parseFloat(params?.find(p => p.name.includes("Длина ската 2 (H2)"))?.value) || 0;

      if (w3 + h2 > w1 && w1 !== 0 && w3 !== 0 && h2 !== 0) {
        return "Ошибка: Ширина конька (W3) + Длина ската 2 (H2) не могут быть больше Ширины ската 1 (W1)";
      }
    }
    return "";
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 850);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const selectedRoof = useMemo(() => {
    return typeOptions.find((option) => option.value === typeRoof) || null;
  }, [typeRoof, typeOptions]);

  const parameters = useMemo(() => {
    if (!selectedRoof) return {};
    return (selectedRoof.parameters || []).reduce((acc, param, index) => {
      acc[`param${index + 1}`] = parseFloat(param?.value) || 0;
      return acc;
    }, {});
  }, [selectedRoof]);

  const parametersString = useMemo(
    () => JSON.stringify(parameters),
    [parameters]
  );

  const calculateDeliveryCost = (distanceFromFactory, totalCost, region) => {
    if (region === 'МСК') {
      return 1500;
    }
    if (region === 'КРД') {
      return 3000;
    }

    const distance = distanceFromFactory ? Math.ceil(distanceFromFactory) : 1000;

    if (distance < 100) {
      if (totalCost >= 0 && totalCost < 45000) return 1000;
      if (totalCost >= 45000 && totalCost < 100000) return 3000;
      if (totalCost >= 100000 && totalCost < 200000) return 4000;
    } else if (distance >= 100 && distance < 150) {
      if (totalCost >= 0 && totalCost < 80000) return 1500;
      if (totalCost >= 80000 && totalCost < 180000) return 4000;
      if (totalCost >= 180000 && totalCost < 350000) return 5000;
    }

    return 6000;
  };

  useEffect(() => {
    if (!selectedRoof || !selectedNomenclature?.id_collection) {
      setAlert({
        type: "warning",
        message: "⚠️ Выберите номенклатуру и укажите все параметры!",
      });
      setRows([]);
      return;
    }

    if (Object.values(parameters).some((value) => value === 0)) {
      setAlert({
        type: "warning",
        message: "⚠️ Все параметры должны быть заполнены и больше 0!",
      });
      setRows([]);
      return;
    }

    // Валидация для вальмовой кровли
    const validationError = validateHipRoof();
    if (validationError) {
      setAlert({
        type: "danger",
        message: validationError,
      });
      setRows([]);
      return;
    }

    setLoading(true);
    const controller = new AbortController();

    fetch(`${PATH}/api/main_calculation_function`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id_collection: String(selectedNomenclature.id_collection),
        type_roof: typeRoof,
        parameters,
        selectedNomenclature,
        region,
      }),
      signal: controller.signal,
    })
      .then((res) =>
        res.ok ? res.json() : res.json().then((err) => Promise.reject(err))
      )
      .then((data) => {
        if (!data.success) throw new Error(data.message || "Ошибка расчета!");
        setAlert({
          type: "success",
          message: "✅ Материалы успешно рассчитаны!",
        });
        const formattedRows = data.formattedItems.map((item, index) => {
          let price = parseFloat(
            item.price && item.price.includes(",")
              ? item.price.replace(",", ".")
              : item.price
          ) || 0;

          if (price === 0 && dobornye) {
            const dobornyeItem = dobornye.find(
              (dobItem) => dobItem[0] === item.name
            );
            if (dobornyeItem) {
              const priceIndex = region === "МСК" ? 13 : region === "КРД" ? 14 : 7;
              const rawPrice = dobornyeItem[priceIndex];
              const formattedPrice = String(rawPrice).includes(",")
                ? rawPrice.replace(",", ".")
                : rawPrice;
              price = parseFloat(formattedPrice) || 0;
              price = parseFloat(price.toFixed(2));
            }
          }

          return {
            id: index + 1,
            name: item.name,
            unit: item.unit || "-",
            quantity: index === 0 ? Math.ceil(sum) : item.value,
            id_bitrix: item.idBitrix,
            price: price,
            recommended_price: parseFloat(item.recommended_price) || 0,
            initial_price: price,
            description: item.description,
          };
        });

        const deliveryRow = formattedRows.find((row) => row.name === "Доставка");
        if (deliveryRow) {
          const totalCostWithoutDelivery = formattedRows
            .filter((row) => row.name !== "Доставка")
            .reduce((sum, row) => sum + row.price * row.quantity, 0);

          const deliveryCost = calculateDeliveryCost(
            distanceFromFactory,
            totalCostWithoutDelivery,
            region
          );

          deliveryRow.recommended_price = deliveryCost;
          deliveryRow.price = deliveryCost;
          deliveryRow.initial_price = deliveryCost;
        }

        setRows(formattedRows);
        setInitialRows(formattedRows);
        setAlert({
          type: "success",
          message: "✅ Материалы успешно рассчитаны!",
        });
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("❌ Ошибка загрузки данных:", error.message);
          setAlert({ type: "danger", message: error.message });
          setRows([]);
        }
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [
    selectedRoof,
    parametersString,
    selectedNomenclature?.id_collection,
    selectedNomenclature,
    distanceFromFactory,
    parameters,
    typeRoof,
    region,
    sum
  ]);

  useEffect(() => {
    const deliveryRow = rows.find((row) => row.name === "Доставка");

    if (deliveryRow) {
      const totalCostWithoutDelivery = rows
        .filter((row) => row.name !== "Доставка")
        .reduce((sum, row) => sum + row.price * row.quantity, 0);

      const deliveryCost = calculateDeliveryCost(
        distanceFromFactory,
        totalCostWithoutDelivery,
        region
      );

      if (deliveryRow.price !== deliveryCost) {
        const updatedRows = rows.map((row) =>
          row.name === "Доставка"
            ? { ...row, initial_price: deliveryCost }
            : row
        );

        if (JSON.stringify(updatedRows) !== JSON.stringify(rows)) {
          setRows(updatedRows);
        }
      }
    }
  }, [rows, distanceFromFactory]);

  const handleDeleteRow = useCallback(
    (id) => {
      setRows((prevRows) => {
        const newRows = prevRows.filter((row) => row.id !== id);
        setHasChanges(newRows.length !== initialRows.length);
        return newRows;
      });
    },
    [initialRows]
  );

  const handlePriceChange = useCallback((id, value) => {
    let parsedValue = parseFloat(value);

    if (isNaN(parsedValue)) parsedValue = 0;
    if (parsedValue < 0) parsedValue = 0;
    if (parsedValue > 1000000) parsedValue = 1000000;

    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id ? { ...row, price: parsedValue } : row
      )
    );
  }, []);

  const handleResetTable = useCallback(() => {
    setRows(initialRows);
    setHasChanges(false);
    setAlert({
      type: "info",
      message: "🔄 Таблица сброшена к исходному состоянию.",
    });
  }, [initialRows]);

  const handleSendToDeal = useCallback(async () => {
    if (rows.length === 0) {
      setAlert({ type: "warning", message: "⚠️ Нет данных для отправки!" });
      return;
    }

    setLoading(true);
    setAlert({ type: "info", message: "⏳ Отправка данных в сделку..." });

    try {
      const payload = {
        dealId,
        products: rows.map(({ id_bitrix, name, price, quantity }) => ({
          bitrixId: /^[0-9]+$/.test(id_bitrix) ? id_bitrix : null,
          name,
          price,
          quantity,
        })),
      };

      const response = await fetch(`${PATH}/api/sender`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Ошибка при отправке в сделку.");
      }

      setAlert({
        type: "success",
        message: `✅ Успешно отправлено: ${data.bitrixProducts} в сделку, ${data.googleSheetProducts} в Google Таблицу!`,
      });
    } catch (error) {
      console.error("❌ Ошибка при отправке:", error.message);
      setAlert({ type: "danger", message: `❌ Ошибка: ${error.message}` });
    } finally {
      setLoading(false);
    }
  }, [rows, dealId]);

  const totalCost = useMemo(() => {
    return rows.reduce((sum, row) => sum + row.price * row.quantity, 0);
  }, [rows]);

  const totalPurchaseCost = useMemo(() => {
    return rows.reduce((sum, row) => sum + row.initial_price * row.quantity, 0);
  }, [rows]);

  const costDifferencePercentage = useMemo(() => {
      if (totalPurchaseCost === 0) return 0;
      return ((totalCost - totalPurchaseCost) / totalPurchaseCost) * 100;
    }, [totalCost, totalPurchaseCost]);

  const handleCopyIdBitrix = useCallback((idBitrix) => {
    if (idBitrix && idBitrix !== "-") {
      navigator.clipboard.writeText(idBitrix)
        .then(() => {
          setAlert({
            type: "success",
            message: `✅ ID 1C (${idBitrix}) скопирован в буфер обмена!`,
          });
        })
        .catch((err) => {
          console.error("Ошибка копирования:", err);
          setAlert({
            type: "danger",
            message: "❌ Не удалось скопировать ID 1C!",
          });
        });
    } else {
      setAlert({
        type: "warning",
        message: "⚠️ ID 1C отсутствует!",
      });
    }
  }, []);

  return (
    <Container>
      <h2 className="fs-3 mb-4 text-start fst-italic text-secondary">
        Коммерческое предложение / {region}
      </h2>
      {alert && (
        <div className={`alert alert-${alert.type} text-center`}>
          {alert.message}
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-bordered table-hover text-center">
          <thead className="table-light">
            <tr>
              {!isSmallScreen && <th>№</th>}
              <th className="text-start">Наименование</th>
              <th>Ед. изм.</th>
              <th>Кол-во</th>
              <th>Цена за ед.</th>
              <th>Стоимость</th>
              <th>Удалить</th>
            </tr>
          </thead>
          <tbody>
            <AnimatePresence>
              {rows.length > 0 ? (
                rows.map((row) => (
                  <motion.tr
                    key={row.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    {!isSmallScreen && <td>{row.id}</td>}
                    <td
                      className="text-start"
                      data-tooltip-id={`tooltip-${row.name}`}
                    >
                      {row.name}
                    </td>
                    <td>{row.unit}</td>
                    <td>{row.quantity}</td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1000000"
                        value={row.price === 0 ? "" : row.price}
                        style={{ minWidth: "100px" }}
                        onChange={(e) =>
                          handlePriceChange(row.id, e.target.value)
                        }
                        className="form-control form-control-sm"
                      />
                    </td>
                    <td
                      className="text-start"
                      data-tooltip-id={`tooltip-cost-${row.id}`}
                      data-tooltip-html={`Закупочная цена: ${formatNumber(
                        row.initial_price
                      )}`}
                    >
                      {formatNumber(row.price * row.quantity)}
                    </td>
                    <td
                      onClick={() => handleDeleteRow(row.id)}
                      style={{ cursor: "pointer", color: "red" }}
                    >
                      ❌
                    </td>
                    <Tooltip
                      id={`tooltip-cost-${row.id}`}
                      place="top"
                      effect="solid"
                    />
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isSmallScreen ? 6 : 7}>Нет данных</td>
                </tr>
              )}
            </AnimatePresence>
            <tr className="table-active">
              <td colSpan={isSmallScreen ? 4 : 5} className="text-end fw-bold">
                Общая закупочная цена:
              </td>
              <td className="fw-bold">{formatNumber(totalPurchaseCost)}</td>
              <td colSpan={isSmallScreen ? 1 : 2}></td>
            </tr>
            <tr className="table-active">
              <td colSpan={isSmallScreen ? 4 : 5} className="text-end fw-bold">
                Итоговая стоимость:
              </td>
              <td className="fw-bold">{formatNumber(totalCost)}</td>
              <td colSpan={isSmallScreen ? 1 : 2}></td>
            </tr>
            <tr className="table-active">
              <td
                colSpan={isSmallScreen ? 4 : 5}
                className={`text-end fw-bold ${
                  costDifferencePercentage > 10
                    ? "text-success"
                    : costDifferencePercentage >= 0
                    ? "text-warning"
                    : "text-danger"
                }`}
              >
                Маржа:
              </td>
              <td
                className={`fw-bold ${
                  costDifferencePercentage > 10
                    ? "text-success"
                    : costDifferencePercentage >= 0
                    ? "text-warning"
                    : "text-danger"
                }`}
              >
                {formatNumber(costDifferencePercentage)}%
              </td>
              <td colSpan={isSmallScreen ? 1 : 2}></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-center mt-3">
        <Button
          onClick={handleSendToDeal}
          disabled={loading}
          loading={loading}
          label="Отправить в сделку Bitrix"
        />
        {hasChanges && (
          <Button
            onClick={handleResetTable}
            className="ms-2"
            label="Вернуть в первоначальный вид"
          />
        )}
      </div>
    </Container>
  );
}

export default Offer;